import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { MenuItem } from '../types';
import { connectivityManager } from '../network/connectivity';
import { ONLINE_SESSION_TIMEOUT_MS } from './sessionConfig';
import { buildBackendUrl } from '../graphql/client';
import {
  SYNC_STATE_CHANGED_EVENT,
  authorizeSensitiveAccess,
  bootstrapFromServer,
  clearObsoleteLocalPersistence,
  expireAuthSession,
  loadActiveScenario,
  loadAuthState,
  loadCatalog,
  loadSyncState,
  login as loginRequest,
  loginWithPin as loginWithPinRequest,
  logout as logoutRequest,
  refreshCatalogFromServer,
  revalidateAuth,
  syncPendingMutations,
  touchSessionActivity,
  updateProfile as updateProfileRequest,
} from '../data/repository';
import { mapProductsToMenuItems } from '../data/graphqlMappers';
import type { AuthSessionRecord, RuntimeSyncState, ScenarioSnapshot } from '../data/types';
import {
  dispatchRealtimeBrowserEvent,
  REALTIME_CATALOG_EVENT,
  type RealtimeEventPayload,
} from '../realtime/events';

interface AuthContextValue {
  auth: AuthSessionRecord | null;
  scenario: ScenarioSnapshot | null;
  menuItems: MenuItem[];
  syncState: RuntimeSyncState;
  backendReachable: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithPin: (email: string, pin: string) => Promise<void>;
  logout: () => Promise<void>;
  authorizeSensitivePassword: (password: string) => Promise<void>;
  updateProfile: (input: { email: string; displayName: string; currentPassword?: string; password?: string; pin?: string }) => Promise<void>;
  refreshFromLocal: () => Promise<void>;
  requestSync: () => Promise<void>;
  refreshSessionActivity: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const ACTIVITY_TOUCH_THROTTLE_MS = 15 * 1000;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [auth, setAuth] = useState<AuthSessionRecord | null>(null);
  const [scenario, setScenario] = useState<ScenarioSnapshot | null>(null);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [syncState, setSyncState] = useState<RuntimeSyncState>({
    status: 'idle',
    pendingCount: 0,
    conflictCount: 0,
    lastError: null,
    lastSyncedAt: null,
    lastConnectivityCheckAt: null,
    authRequired: false,
    conflictScenarioName: null,
    conflictRemoteVersion: null,
  });
  const [backendReachable, setBackendReachable] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : false,
  );
  const [loading, setLoading] = useState(true);

  async function refreshFromLocal() {
    const [nextAuth, nextScenario, nextMenuItems, nextSyncState] = await Promise.all([
      loadAuthState(),
      loadActiveScenario(),
      loadCatalog(),
      loadSyncState(),
    ]);
    setAuth(nextAuth);
    setScenario(nextScenario);
    setMenuItems(nextMenuItems);
    setSyncState(nextSyncState);
  }

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      setLoading(true);
      clearObsoleteLocalPersistence();
      await refreshFromLocal();
      connectivityManager.start();
      setBackendReachable(connectivityManager.getSnapshot());

      try {
        const currentAuth = await revalidateAuth();
        if (currentAuth.authenticated) {
          await syncPendingMutations();
          await bootstrapFromServer();
          await refreshFromLocal();
        }
      } catch (error) {
        await refreshFromLocal();
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    const unsubscribe = connectivityManager.subscribe((reachable) => {
      setBackendReachable(reachable);
    });

    void bootstrap();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleSyncStateChanged = (event: Event) => {
      const nextState = (event as CustomEvent<RuntimeSyncState>).detail;
      if (nextState) {
        setSyncState(nextState);
      }
    };

    window.addEventListener(SYNC_STATE_CHANGED_EVENT, handleSyncStateChanged as EventListener);
    return () => {
      window.removeEventListener(SYNC_STATE_CHANGED_EVENT, handleSyncStateChanged as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!auth?.authenticated || auth.authExpired || !backendReachable) {
      return;
    }

    let lastTouchAt = 0;

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastTouchAt < ACTIVITY_TOUCH_THROTTLE_MS) {
        return;
      }
      lastTouchAt = now;
      void touchSessionActivity().then((nextAuth) => {
        setAuth(nextAuth);
      });
    };

    document.addEventListener('pointerdown', handleActivity, true);
    document.addEventListener('keydown', handleActivity, true);

    return () => {
      document.removeEventListener('pointerdown', handleActivity, true);
      document.removeEventListener('keydown', handleActivity, true);
    };
  }, [auth?.authenticated, auth?.authExpired, backendReachable]);

  useEffect(() => {
    if (!auth?.authenticated || auth.authExpired || !backendReachable) {
      return;
    }

    void touchSessionActivity().then((nextAuth) => {
      setAuth(nextAuth);
    });
  }, [backendReachable, auth?.authenticated, auth?.authExpired]);

  useEffect(() => {
    if (!auth?.authenticated || auth.authExpired || !backendReachable) {
      return;
    }

    const intervalId = window.setInterval(() => {
      if (!auth.lastActivityAt) {
        return;
      }
      const lastActivityAt = new Date(auth.lastActivityAt).getTime();
      if (Number.isNaN(lastActivityAt)) {
        return;
      }
      if (Date.now() - lastActivityAt < ONLINE_SESSION_TIMEOUT_MS) {
        return;
      }
      void expireAuthSession().then((nextAuth) => {
        setAuth(nextAuth);
      });
    }, 60 * 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [auth, backendReachable]);

  useEffect(() => {
    if (!auth?.authenticated || auth.authExpired || !backendReachable) {
      return;
    }

    const source = new EventSource(buildBackendUrl('/events'), { withCredentials: true });

    const forwardEvent = (event: Event) => {
      const message = event as MessageEvent<string>;
      if (!message.data) {
        return;
      }

      try {
        const payload = JSON.parse(message.data) as RealtimeEventPayload;
        dispatchRealtimeBrowserEvent(payload);
      } catch (_error) {
        // Ignore malformed realtime payloads and keep the stream alive.
      }
    };

    source.addEventListener('scenario-presence', forwardEvent);
    source.addEventListener('scenario-library', forwardEvent);
    source.addEventListener('scenario-snapshot', forwardEvent);
    source.addEventListener('catalog', forwardEvent);

    return () => {
      source.removeEventListener('scenario-presence', forwardEvent);
      source.removeEventListener('scenario-library', forwardEvent);
      source.removeEventListener('scenario-snapshot', forwardEvent);
      source.removeEventListener('catalog', forwardEvent);
      source.close();
    };
  }, [auth?.authenticated, auth?.authExpired, backendReachable]);

  useEffect(() => {
    if (typeof window === 'undefined' || !auth?.authenticated || auth.authExpired || !backendReachable) {
      return;
    }

    const handleRealtimeCatalog = () => {
      void refreshCatalogFromServer()
        .then((products) => {
          setMenuItems(mapProductsToMenuItems(products));
        })
        .catch(() => undefined);
    };

    window.addEventListener(REALTIME_CATALOG_EVENT, handleRealtimeCatalog as EventListener);
    return () => {
      window.removeEventListener(REALTIME_CATALOG_EVENT, handleRealtimeCatalog as EventListener);
    };
  }, [auth?.authenticated, auth?.authExpired, backendReachable]);

  useEffect(() => {
    if (!auth?.authenticated || auth.authExpired || !backendReachable || menuItems.length > 0) {
      return;
    }

    let cancelled = false;

    void refreshCatalogFromServer()
      .then((products) => {
        if (cancelled) {
          return;
        }

        setMenuItems(mapProductsToMenuItems(products));
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [auth?.authenticated, auth?.authExpired, backendReachable, menuItems.length]);

  async function handleLogin(email: string, password: string) {
    setLoading(true);
    try {
      await loginRequest(email, password);
      setBackendReachable(true);
      await refreshFromLocal();
    } finally {
      setLoading(false);
    }
  }

  async function handleLogout() {
    setLoading(true);
    try {
      await logoutRequest();
      await refreshFromLocal();
    } finally {
      setLoading(false);
    }
  }

  async function requestSync() {
    setSyncState((current) => ({ ...current, status: 'syncing' }));
    await syncPendingMutations(scenario?.scenarioName);
    await refreshFromLocal();
  }

  async function handleUpdateProfile(input: { email: string; displayName: string; currentPassword?: string; password?: string; pin?: string }) {
    setLoading(true);
    try {
      await updateProfileRequest(input);
      await refreshFromLocal();
    } finally {
      setLoading(false);
    }
  }

  async function handleLoginWithPin(email: string, pin: string) {
    setLoading(true);
    try {
      await loginWithPinRequest(email, pin);
      setBackendReachable(true);
      await refreshFromLocal();
    } finally {
      setLoading(false);
    }
  }

  async function refreshSessionActivity() {
    const nextAuth = await touchSessionActivity();
    setAuth(nextAuth);
  }

  async function handleAuthorizeSensitivePassword(password: string) {
    const nextAuth = await authorizeSensitiveAccess(password);
    setAuth(nextAuth);
  }

  const value = useMemo<AuthContextValue>(() => ({
    auth,
    scenario,
    menuItems,
    syncState,
    backendReachable,
    loading,
    login: handleLogin,
    loginWithPin: handleLoginWithPin,
    logout: handleLogout,
    authorizeSensitivePassword: handleAuthorizeSensitivePassword,
    updateProfile: handleUpdateProfile,
    refreshFromLocal,
    requestSync,
    refreshSessionActivity,
  }), [auth, scenario, menuItems, syncState, backendReachable, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
